import { motion } from "framer-motion";
import { Eye, ScanSearch, Layers, ShieldCheck } from "lucide-react";
import { BlueprintGrid } from "../components/BlueprintGrid";
import { PRINCIPLES, AI_TRANSPARENCY_NOTE } from "../lib/about";
import {
    fadeUp,
    viewportOnce,
    maskContainer,
    maskLine,
    stagger,
} from "../lib/motion";

const ICONS = { Eye, ScanSearch, Layers, ShieldCheck };

const PrincipleBand = () => (
    <section
        data-testid="principle-band"
        className="relative overflow-hidden border-y border-white/10 bg-ink py-24 lg:py-36"
    >
        <BlueprintGrid />
        <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="flex items-center gap-6"
            >
                <p className="font-mono text-[13px] tracking-[0.3em] text-mist">
                    OUR APPROACH
                </p>
                <span className="h-px flex-1 bg-white/10" />
            </motion.div>

            <motion.h2
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                data-testid="approach-headline"
                className="mt-12 max-w-4xl font-display text-4xl font-semibold leading-[1.1] text-white md:text-6xl"
            >
                Built around the homeowner. Guided by intelligence
                <span className="text-accent">.</span>
            </motion.h2>

            <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4"
            >
                {PRINCIPLES.map((p) => {
                    const Icon = ICONS[p.icon];
                    return (
                        <motion.div
                            key={p.title}
                            variants={fadeUp}
                            data-testid={`approach-principle-${p.title.toLowerCase()}`}
                            className="flex flex-col border border-white/12 bg-surface/80 px-7 py-8"
                        >
                            <span className="flex h-12 w-12 items-center justify-center border border-white/15 text-mist">
                                <Icon
                                    className="h-5 w-5"
                                    strokeWidth={1.5}
                                    aria-hidden="true"
                                />
                            </span>
                            <h3 className="mt-6 font-mono text-[13px] tracking-[0.3em] text-white">
                                {p.title}
                            </h3>
                            <p className="mt-4 flex-1 text-[14px] leading-relaxed text-mist">
                                {p.copy}
                            </p>
                        </motion.div>
                    );
                })}
            </motion.div>

            <motion.h2
                data-testid="principle-headline"
                className="mt-24 font-display text-[clamp(2rem,5.5vw,5.5rem)] font-bold leading-[1.05] text-white"
                variants={maskContainer}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
            >
                <span className="block overflow-hidden">
                    <motion.span className="block" variants={maskLine}>
                        <span className="text-accent">“</span>IF WE CAN'T
                        VERIFY IT,
                    </motion.span>
                </span>
                <span className="block overflow-hidden">
                    <motion.span className="block" variants={maskLine}>
                        WE DON'T PRETEND TO KNOW IT
                        <span className="text-accent">.</span>
                        <span className="text-accent">”</span>
                    </motion.span>
                </span>
            </motion.h2>

            <motion.p
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                data-testid="ai-transparency-note"
                className="mt-12 max-w-2xl border-l border-accent/50 pl-5 font-mono text-[13px] leading-relaxed tracking-[0.14em] text-mist/80"
            >
                {AI_TRANSPARENCY_NOTE}
            </motion.p>
        </div>
    </section>
);

export default PrincipleBand;
